import { useMemo, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'

import {
  AccordionRoot,
  Box,
  type BoxProps,
  AccordionItem as EcAccordionItem,
  AccordionItemContent as EcAccordionItemContent,
  AccordionItemTrigger as EcAccordionItemTrigger,
  Flex,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { AccordionDisclosureIcon } from '~/components/common/AccordionDisclosureIcon'
import { CompactAlert } from '~/components/common/CompactAlert'
import { Modal } from '~/components/common/Modal'
import { MultiSelect } from '~/components/common/MultiSelect'
import { RegionLoading } from '~/components/common/RegionLoading'
import { FilterSection } from '~/components/common/filters/FilterToolbar'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { usePersistedFilters } from '~/hooks/usePersistedFilters'
import { convertAreaLabel } from '~/models/area'
import { formatJstDateTimeFull } from '~/models/dateTime'
import { OperationHistoryTable } from '~/pages/SpotBidding/components/OperationHistoryTable'
import { useListBiddingSpotOperationLogs } from '~/pages/SpotBidding/hooks/useListBiddingSpotOperationLogs'
import { keepSelectableAreas } from '~/pages/SpotBidding/models/executionResultFilters'
import {
  OPERATION_HISTORY_BID_KINDS,
  OPERATION_HISTORY_OPERATIONS,
  type OperationHistoryBidKind,
  type OperationHistoryFilter,
  type OperationHistoryOperation,
  type OperationHistoryRow,
  filterOperationHistoryRows,
  isSystemOperation,
  operationHistoryCount,
  regroupOperationHistoryRows,
  toOperationHistoryRows,
} from '~/pages/SpotBidding/models/operationHistory'
import {
  SPOT_OPERATION_HISTORY_FILTER_STORAGE_KEY,
  operationHistoryFilterCodec,
} from '~/pages/SpotBidding/models/operationHistoryFilters'

type Props = {
  /** 受渡日 (共通ツールバーで選んだ日)。 */
  deliveryDate: ProtoDate
  /** エリアの選択肢 (= 業務エリア設定で許可された JEPX 取引対象エリア。 約定結果タブと同じ)。 */
  areaCodes: readonly AreaCode[]
}

// chakra v3.29 + React 18 types の既知不具合で Accordion item 系の props が collapse する
// (ExecutionResultTab と同じ cast)。
const AccordionItem = EcAccordionItem as React.FC<BoxProps & { value: string }>
const AccordionItemTrigger = EcAccordionItemTrigger as React.FC<BoxProps>
const AccordionItemContent = EcAccordionItemContent as React.FC<BoxProps>

const SIDE_VALUES = [Side.BUY, Side.SELL] as const

/**
 * オペレーション履歴タブ (AC-BSP-038)。
 *
 * 選んだ受渡日の通常入札・ブロック入札の操作 (申請取消 / 差戻し後再編集 / 取下 / 予約取消) を、
 * 操作した時点の札の値で 1 つの表に出す。 共通ツールバーのエリア・発注主体区分では絞らず、
 * タブ内のフィルタ (入札種別 / 操作 / エリア / 売買) だけが効く (AC-BSP-131)。
 * 行を押すと同じバッチ (= 同じログ行) の全行を同じ列で並べたダイアログを開く。
 *
 * design: `design/bidding/spot.html` の `#tab-oplog`。 CSV ダウンロードは別 slice。
 */
export const OperationHistoryTab = ({
  deliveryDate,
  areaCodes,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const resolveMemberName = useMemberDisplayName()
  const resolveOperatorName = (row: OperationHistoryRow): string | undefined =>
    isSystemOperation(row.operation)
      ? t('page:biddingSpot.operationHistory.systemOperator')
      : resolveMemberName(row.operatedBy)
  const { data, isPending, isFetching, isError } =
    useListBiddingSpotOperationLogs(deliveryDate)

  // タブ内のフィルタは AC-SC-031 に従い保持し、 タブの切り替え・他の画面への移動・再読み込みのあとに
  // 復元する (#12745)。 エリアは業務エリア外のものを表示と絞り込みの時だけ外す (約定結果タブと同じ)。
  const { filters: savedFilter, updateFilters } =
    usePersistedFilters<OperationHistoryFilter>({
      storageKey: SPOT_OPERATION_HISTORY_FILTER_STORAGE_KEY,
      codec: operationHistoryFilterCodec,
    })
  const [detailBatchId, setDetailBatchId] = useState<string | undefined>()

  // 業務エリア外の行は出さない (`conventions.md`「業務エリアフィルタの原則」。 約定結果タブと同じ)。
  const allowedAreas = useMemo(() => new Set(areaCodes), [areaCodes])
  const rows = useMemo(
    () =>
      toOperationHistoryRows(data?.items ?? []).filter((row) =>
        allowedAreas.has(row.areaCode),
      ),
    [data, allowedAreas],
  )
  const [isOpen, setIsOpen] = useState(true)
  const filter = useMemo<OperationHistoryFilter>(
    () => ({
      ...savedFilter,
      areas: keepSelectableAreas(savedFilter.areas, areaCodes),
    }),
    [savedFilter, areaCodes],
  )
  const shown = useMemo(
    () => regroupOperationHistoryRows(filterOperationHistoryRows(rows, filter)),
    [rows, filter],
  )
  const count = operationHistoryCount(shown.length, rows.length, filter)
  const countLabel =
    count.kind === 'all'
      ? t('page:biddingSpot.operationHistory.count', { count: count.total })
      : t('page:biddingSpot.operationHistory.countFiltered', {
          shown: count.shown,
          total: count.total,
        })

  const detailRows = useMemo<OperationHistoryRow[]>(
    () =>
      detailBatchId === undefined
        ? []
        : regroupOperationHistoryRows(
            rows.filter((row) => row.batchId === detailBatchId),
          ),
    [rows, detailBatchId],
  )
  const detailHead = detailRows[0]

  return (
    <VStack align="stretch" gap={3}>
      <AccordionRoot
        collapsible
        onValueChange={(e: { value: string[] }) =>
          setIsOpen(e.value.includes('oplog'))
        }
        value={isOpen ? ['oplog'] : []}
      >
        <AccordionItem
          overflow="hidden"
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="lg"
          backgroundColor="Surface.primary"
          value="oplog"
        >
          {/* design `#spot-oplog-accordion`: 見出し・件数・フィルタを trigger 行に載せる。
              フィルタ (button / combobox) は trigger の button の中に入れ子にできないので
              trigger の隣に置き、 閉じている間は出さない。 CSV ダウンロードボタン
              (`#spot-oplog-csv`) の場所はフィルタの右に空けてある (Slice 52)。 */}
          <Flex alignItems="center" flexWrap="wrap" gap={3} paddingRight={4}>
            <AccordionItemTrigger flex="0 1 auto" paddingX={4} paddingY={3}>
              <AccordionDisclosureIcon />
              <Text textStyle="sectionTitle" flexShrink={0}>
                {t('page:biddingSpot.operationHistory.title')}
                {!isPending && !isError && (
                  <Text
                    as="span"
                    textStyle="captionNormal"
                    marginLeft={2}
                    color="Text.midEmphasis"
                    css={{ fontVariantNumeric: 'tabular-nums' }}
                    data-testid="operation-history-count"
                  >
                    {countLabel}
                  </Text>
                )}
              </Text>
            </AccordionItemTrigger>
            {isOpen && (
              <Flex
                alignItems="center"
                flexWrap="wrap"
                gap={3}
                marginLeft="auto"
              >
                <FilterSection
                  label={t('page:biddingSpot.operationHistory.filter.bidKind')}
                >
                  <Box width="130px">
                    <MultiSelect
                      aria-label={t(
                        'page:biddingSpot.operationHistory.filter.bidKind',
                      )}
                      onChange={(next) =>
                        updateFilters((prev) => ({
                          ...prev,
                          kinds: new Set(next as OperationHistoryBidKind[]),
                        }))
                      }
                      options={OPERATION_HISTORY_BID_KINDS.map((kind) => ({
                        value: kind,
                        label: t(
                          `page:biddingSpot.operationHistory.kind.${kind}`,
                        ),
                      }))}
                      placeholder={t(
                        'page:biddingSpot.sidebar.filterStatusAll',
                      )}
                      selected={Array.from(filter.kinds)}
                    />
                  </Box>
                </FilterSection>
                <FilterSection
                  label={t(
                    'page:biddingSpot.operationHistory.filter.operation',
                  )}
                >
                  <Box width="170px">
                    <MultiSelect
                      aria-label={t(
                        'page:biddingSpot.operationHistory.filter.operation',
                      )}
                      onChange={(next) =>
                        updateFilters((prev) => ({
                          ...prev,
                          operations: new Set(
                            next as OperationHistoryOperation[],
                          ),
                        }))
                      }
                      options={OPERATION_HISTORY_OPERATIONS.map(
                        (operation) => ({
                          value: operation,
                          label: t(
                            `page:biddingSpot.operationHistory.operation.${operation}`,
                          ),
                        }),
                      )}
                      placeholder={t(
                        'page:biddingSpot.sidebar.filterStatusAll',
                      )}
                      selected={Array.from(filter.operations)}
                    />
                  </Box>
                </FilterSection>
                <FilterSection
                  label={t('page:biddingSpot.executionResult.filter.area')}
                >
                  <Box width="140px">
                    <MultiSelect
                      aria-label={t(
                        'page:biddingSpot.executionResult.filter.area',
                      )}
                      onChange={(next) =>
                        updateFilters((prev) => ({
                          ...prev,
                          areas: new Set(
                            next.map((v) => Number(v) as AreaCode),
                          ),
                        }))
                      }
                      options={areaCodes.map((code) => ({
                        value: String(code),
                        label: convertAreaLabel(code),
                      }))}
                      placeholder={t(
                        'page:biddingSpot.sidebar.filterStatusAll',
                      )}
                      selected={Array.from(filter.areas, String)}
                    />
                  </Box>
                </FilterSection>
                <FilterSection label={t('page:biddingSpot.sidebar.filterSide')}>
                  <Box width="110px">
                    <MultiSelect
                      aria-label={t('page:biddingSpot.sidebar.filterSide')}
                      onChange={(next) =>
                        updateFilters((prev) => ({
                          ...prev,
                          sides: new Set(next.map((v) => Number(v) as Side)),
                        }))
                      }
                      options={SIDE_VALUES.map((side) => ({
                        value: String(side),
                        label: t(
                          side === Side.BUY
                            ? 'page:biddingSpot.side.buy'
                            : 'page:biddingSpot.side.sell',
                        ),
                      }))}
                      placeholder={t(
                        'page:biddingSpot.sidebar.filterStatusAll',
                      )}
                      selected={Array.from(filter.sides, String)}
                    />
                  </Box>
                </FilterSection>
              </Flex>
            )}
          </Flex>
          <AccordionItemContent
            padding={0}
            borderTopWidth="1px"
            borderTopColor="Border.lowEmphasis"
          >
            {isError ? (
              <CompactAlert
                status="error"
                title={t('page:biddingSpot.operationHistory.loadError')}
              />
            ) : (
              <RegionLoading isLoading={isPending || isFetching}>
                {!isPending && (
                  <OperationHistoryTable
                    onRowClick={(row) => setDetailBatchId(row.batchId)}
                    resolveOperatorName={resolveOperatorName}
                    rows={shown}
                  />
                )}
              </RegionLoading>
            )}
          </AccordionItemContent>
        </AccordionItem>
      </AccordionRoot>

      <Modal
        footer={
          <TertiaryButton onClick={() => setDetailBatchId(undefined)}>
            {t('common:action.close')}
          </TertiaryButton>
        }
        isOpen={detailBatchId !== undefined}
        onClose={() => setDetailBatchId(undefined)}
        size="full"
        title={t('page:biddingSpot.operationHistory.detail.title')}
      >
        {detailHead !== undefined && (
          <VStack align="stretch" gap={3}>
            <Text color="Text.midEmphasis" fontSize="md">
              {t('page:biddingSpot.operationHistory.detail.summary', {
                operatedAt:
                  detailHead.operatedAt === undefined
                    ? '—'
                    : formatJstDateTimeFull(detailHead.operatedAt),
                operatedBy: resolveOperatorName(detailHead) ?? '—',
                operation: t(
                  `page:biddingSpot.operationHistory.operation.${detailHead.operation}`,
                ),
                count: detailRows.length,
              })}
            </Text>
            <OperationHistoryTable
              resolveOperatorName={resolveOperatorName}
              rows={detailRows}
            />
          </VStack>
        )}
      </Modal>
    </VStack>
  )
}
